// frontend/js/holoModal.js
// Holographic modal system for Emotional Debt notifications

let _lockScrollY = 0;

function getLockCount() {
  return Number(document.body.dataset.holoLockCount || 0);
}

function setLockCount(n) {
  document.body.dataset.holoLockCount = String(n);
}

function lockScrollIfNeeded() {
  const count = getLockCount();
  if (count === 0) {
    _lockScrollY = window.scrollY || window.pageYOffset || 0;
    const inline = document.body.style.overflow;
    document.body.dataset.holoPrevOverflow = inline || '';

    // iOS-friendly lock: fix body in place
    document.body.style.position = 'fixed';
    document.body.style.top = `-${_lockScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  setLockCount(count + 1);
}

function unlockScrollIfNeeded() {
  const count = getLockCount();
  const next = Math.max(0, count - 1);
  setLockCount(next);
  if (next === 0) {
    const prev = document.body.dataset.holoPrevOverflow || '';

    // Restore before scrolling back
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = prev;

    window.scrollTo(0, _lockScrollY);
    delete document.body.dataset.holoPrevOverflow;
  }
}

/**
 * Create and show a holographic modal
 * @param {Object} options - Modal configuration
 * @param {string} options.title - Modal title
 * @param {string} options.message - Modal message (supports HTML)
 * @param {string} options.type - Modal type: 'success', 'warning', 'danger' (default: 'success')
 * @param {Array} options.buttons - Array of button configs: [{ text, onClick, type: 'primary'|'danger' }]
 * @param {boolean} options.autoClose - Auto-close after 5 seconds (default: true)
 */
 
export function showHoloModal({
   title = 'Notification',
   message = '',
   type = 'success',
   buttons = null,
   autoClose = true
 }) {
   // If one exists, we’re replacing it; don’t double-lock
   const existing = document.querySelector('.holo-modal-overlay');
   const replacingExisting = !!existing;
   if (existing) existing.remove();
 
   // Create overlay
   const overlay = document.createElement('div');
   overlay.className = 'holo-modal-overlay';
   overlay.style.overflow = 'hidden'; // modal body will handle scrolling
 
   // Create modal
   const modal = document.createElement('div');
   modal.className = `holo-modal holo-${type}`;
 
   // Accessibility
   overlay.setAttribute('role', 'presentation');
   modal.setAttribute('role', 'dialog');
   modal.setAttribute('aria-modal', 'true');
   modal.setAttribute('aria-label', title);
 
   // Modal content
   const content = document.createElement('div');
   content.className = 'holo-modal-content';
 
   // Header
   const header = document.createElement('div');
   header.className = 'holo-modal-header';
   const titleEl = document.createElement('h3');
   titleEl.className = 'holo-modal-title';
   titleEl.textContent = title;
   header.appendChild(titleEl);
 
   // Body
   const body = document.createElement('div');
   body.className = 'holo-modal-body';
   body.innerHTML = message;
 
   // Footer with buttons
   const footer = document.createElement('div');
   footer.className = 'holo-modal-footer';
 
   if (buttons && buttons.length > 0) {
     buttons.forEach(btn => {
       const button = document.createElement('button');
       button.className = `holo-btn ${btn.type ? 'holo-btn-' + btn.type : ''}`;
       button.textContent = btn.text || 'OK';
       button.onclick = (e) => {
         e.stopPropagation();
         if (btn.onClick) btn.onClick();
         closeModal();
       };
       footer.appendChild(button);
     });
   } else {
     const okBtn = document.createElement('button');
     okBtn.className = 'holo-btn holo-btn-primary';
     okBtn.textContent = 'OK';
     okBtn.onclick = (e) => {
       e.stopPropagation();
       closeModal();
     };
     footer.appendChild(okBtn);
   }
 
   // Assemble
   content.appendChild(header);
   content.appendChild(body);
   content.appendChild(footer);
   modal.appendChild(content);
   overlay.appendChild(modal);
   document.body.appendChild(overlay);

   // Generate unique ID for tracking
   const modalId = `holoModal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
   overlay.id = modalId;

   // Track popup state for bot interaction management
   if (window.popupTracker && !replacingExisting) {
     window.popupTracker.trackElement(overlay, 'holo-modal');
   }

   // Scroll lock: only lock if we weren't replacing an existing modal
   if (!replacingExisting) {
     lockScrollIfNeeded();
   }
 
   // Close on overlay click (but not on internal clicks)
   overlay.onclick = (e) => {
     if (e.target === overlay) {
       e.stopPropagation();
       closeModal();
     }
   };
 
   // Prevent scroll chaining on overlay (swipes should stay in the body scroller)
   overlay.addEventListener('touchmove', (e) => {
     const scroller = e.target.closest('.holo-modal-body');
     if (!scroller) {
       e.preventDefault();
     }
   }, { passive: false });
 
   // Auto-close
   if (autoClose && !buttons) {
     setTimeout(closeModal, 5000);
   }
 
   let isClosing = false;
   function closeModal() {
     if (isClosing) return;
     isClosing = true;

     // Untrack popup before removing
     if (window.popupTracker && overlay.id) {
       window.popupTracker.unregisterPopup(overlay.id);
     }

     overlay.style.animation = 'holo-fade-in 0.2s ease reverse';
     setTimeout(() => {
       if (overlay.parentNode) overlay.remove();

       // Unlock scroll if no more overlays remain
       const remaining = document.querySelectorAll('.holo-modal-overlay').length;
       if (remaining === 0) {
         unlockScrollIfNeeded();
       }
     }, 200);
   }
 
   return { overlay, modal, closeModal };
 }

/**
 * Show a confirmation modal with Accept/Decline buttons
 */
export function showHoloConfirm({
  title = 'Confirm',
  message = '',
  type = 'warning',
  onAccept = () => {},
  onDecline = () => {},
  acceptText = 'Accept',
  declineText = 'Decline'
}) {
  return showHoloModal({
    title,
    message,
    type,
    autoClose: false,
    buttons: [
      {
        text: declineText,
        type: 'danger',
        onClick: onDecline
      },
      {
        text: acceptText,
        type: 'primary',
        onClick: onAccept
      }
    ]
  });
}

/**
 * Quick notification (auto-closes)
 */
export function holoNotify(message, type = 'success') {
  return showHoloModal({
    title: type === 'success' ? 'Success' : type === 'danger' ? 'Error' : 'Notice',
    message,
    type,
    autoClose: true
  });
}

export default { showHoloModal, showHoloConfirm, holoNotify };
